import React from 'react';

export type FooterState = 'default' | 'correct' | 'incorrect' | 'answer-revealed';

interface ExerciseFooterProps {
  state?: FooterState;
  buttonText?: string;
  buttonEnabled?: boolean;
  xpAmount?: number;
  isLastQuestion?: boolean;
  onContinue?: () => void;
  onWhy?: () => void;
  onTryAgain?: () => void;
  onSeeAnswer?: () => void;
  onSkipExplanation?: () => void;
  darkMode?: boolean;
}

const ExerciseFooter: React.FC<ExerciseFooterProps> = ({
  state = 'default',
  buttonText = 'Continue',
  buttonEnabled = true,
  xpAmount = 15,
  isLastQuestion = false,
  onContinue,
  onWhy,
  onTryAgain,
  onSeeAnswer,
  onSkipExplanation,
  darkMode = true,
}) => {
  const continueButtonText = isLastQuestion ? 'Finish' : 'Continue';
  const skipButtonText = isLastQuestion ? 'Finish' : 'Skip explanation';

  let backgroundColor = darkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF';
  if (state === 'correct') backgroundColor = darkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(0, 205, 156, 0.15)';
  else if (state === 'incorrect') backgroundColor = darkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255, 199, 0, 0.25)';
  else if (state === 'answer-revealed') backgroundColor = darkMode ? 'rgba(100, 116, 139, 0.15)' : 'rgba(220, 230, 236, 0.5)';

  const borderColor = darkMode ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb';
  const textColor = darkMode ? '#94a3b8' : '#6b7280';

  const primaryButtonClass = `
    px-8 py-3 rounded-xl font-bold text-white transition-all
    ${state === 'correct' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-blue-500 hover:bg-blue-600'}
    ${!buttonEnabled ? 'opacity-50 cursor-not-allowed' : ''}
  `;

  const secondaryButtonClass = `
    px-6 py-3 rounded-xl font-semibold transition-all
    ${darkMode ? 'text-slate-300 bg-slate-700 hover:bg-slate-600' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}
  `;

  return (
    <div
      className="w-full px-8 py-6"
      style={{
        backgroundColor,
        borderTop: `1px solid ${borderColor}`,
      }}
    >
      {/* Default state: Just the main button */}
      {state === 'default' && (
        <div className="flex justify-center">
          <button
            className={primaryButtonClass}
            disabled={!buttonEnabled}
            onClick={onContinue}
          >
            {buttonText}
          </button>
        </div>
      )}

      {/* Correct state: Celebration, XP, Why?, Continue */}
      {state === 'correct' && (
        <div className="flex items-center justify-between">
          {/* Left: Celebration and XP */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🎉</span>
              <span className="text-xl font-bold text-emerald-500">Correct!</span>
            </div>
            <div className="flex items-center gap-1 text-emerald-500">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L9.5 9H2l6 4.5L5.5 22 12 17l6.5 5-2.5-8.5 6-4.5h-7.5L12 2z"/>
              </svg>
              <span className="font-bold">+{xpAmount} XP</span>
            </div>
          </div>

          {/* Right: Buttons */}
          <div className="flex items-center gap-3">
            <button className={secondaryButtonClass} onClick={onWhy}>
              Why?
            </button>
            <button className={primaryButtonClass} onClick={onContinue}>
              {continueButtonText}
            </button>
          </div>
        </div>
      )}

      {/* Incorrect state: Message, Try again, See answer */}
      {state === 'incorrect' && (
        <div className="flex flex-col gap-4">
          <p className={`text-lg font-medium ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
            That's incorrect. Try again.
          </p>
          <div className="flex items-center gap-3">
            <button
              className="px-8 py-3 rounded-xl font-bold text-white bg-blue-500 hover:bg-blue-600 transition-all"
              onClick={onTryAgain}
            >
              Try again
            </button>
            <button
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={onSeeAnswer}
            >
              See answer
            </button>
          </div>
        </div>
      )}

      {/* Answer revealed state: Here's the answer, Why?, Skip explanation */}
      {state === 'answer-revealed' && (
        <div className="flex items-center justify-between">
          {/* Left: Message */}
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6" style={{ color: textColor }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path strokeLinecap="round" d="M21 21l-4.35-4.35"/>
            </svg>
            <span className={`text-lg font-bold ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              Here's the answer
            </span>
          </div>

          {/* Right: Buttons */}
          <div className="flex items-center gap-3">
            <button
              className="px-8 py-3 rounded-xl font-bold text-white bg-blue-500 hover:bg-blue-600 transition-all"
              onClick={onWhy}
            >
              Why?
            </button>
            <button className={secondaryButtonClass} onClick={onSkipExplanation}>
              {skipButtonText}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseFooter;
